import { useEffect, useState } from 'react'
import type { ReinvestTargetInput } from '@/types/simulation'

// Pct-share (0.3) → integer-percent display ("30"), float-safe via toFixed.
function shareToPctDisplay(share: string): string {
  const n = Number(share)
  if (!Number.isFinite(n) || n <= 0) return ''
  return Number((n * 100).toFixed(2)).toString()
}

// String-display ("30") → pct-share string ("0.3"), float-safe via toFixed.
function pctDisplayToShare(display: string): string {
  if (display === '') return '0'
  const n = Number(display)
  if (!Number.isFinite(n)) return '0'
  return Number((n / 100).toFixed(4)).toString()
}

export interface SimulationPosition {
  id: number
  name: string
  ticker: string | null
  total_quantity: string
  position_value_eur: string
}

interface Props {
  positions: SimulationPosition[]
  selectedPositionId: number | null
  onPositionChange: (id: number | null) => void
  sellPct: string
  onSellPctChange: (pct: string) => void
  reinvestTargets: ReinvestTargetInput[]
  onReinvestTargetsChange: (targets: ReinvestTargetInput[]) => void
  onQuickFill: () => void
  isQuickFillLoading: boolean
  newAssetOptions?: { isin: string; label: string }[]
}

const INPUT_CLS =
  'w-full border border-hairline bg-void px-3 py-2 font-mono text-sm text-ink placeholder:text-faint transition-colors focus:border-bernstein focus:outline-none disabled:opacity-50'

const SECONDARY_BTN =
  'border border-hairline px-3 py-1.5 text-[11px] text-ink transition-colors hover:bg-panel disabled:cursor-not-allowed disabled:opacity-50'

export function WhatIfForm({
  positions,
  selectedPositionId,
  onPositionChange,
  sellPct,
  onSellPctChange,
  reinvestTargets,
  onReinvestTargetsChange,
  onQuickFill,
  isQuickFillLoading,
  newAssetOptions = [],
}: Props) {
  const selected = positions.find((p) => p.id === selectedPositionId) ?? null

  const updateTarget = (idx: number, patch: Partial<ReinvestTargetInput>) => {
    const next = [...reinvestTargets]
    next[idx] = { ...next[idx], ...patch }
    onReinvestTargetsChange(next)
  }

  const addTarget = () => {
    onReinvestTargetsChange([...reinvestTargets, { position_id: null, is_cash: true, pct: '0' }])
  }

  const removeTarget = (idx: number) => {
    onReinvestTargetsChange(reinvestTargets.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-5">
      <div className="tracked-caps text-[10px] text-faint">Eingabe</div>

      <div>
        <label className="tracked-caps text-[10px] text-muted">Position (Sell)</label>
        <select
          className={`mt-2 ${INPUT_CLS}`}
          value={selectedPositionId ?? ''}
          onChange={(e) => onPositionChange(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">– bitte wählen –</option>
          {positions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.ticker ?? p.name}
            </option>
          ))}
        </select>
        {selected && (
          <div className="mt-2 font-mono text-xs text-muted">
            Bestand: {selected.total_quantity} Stk · {selected.position_value_eur} €
          </div>
        )}
      </div>

      <SellPctInput sellPct={sellPct} onSellPctChange={onSellPctChange} />

      <div>
        <div className="flex items-center justify-between">
          <label className="tracked-caps text-[10px] text-muted">Reinvest-Targets</label>
          <button
            type="button"
            onClick={onQuickFill}
            disabled={isQuickFillLoading || !selectedPositionId}
            className={SECONDARY_BTN}
          >
            {isQuickFillLoading ? '...' : 'Quick-Fill aus Drift'}
          </button>
        </div>
        <ReinvestTotal targets={reinvestTargets} />
        <div className="mt-2 space-y-2 border border-hairline bg-void p-3">
          {reinvestTargets.length === 0 && (
            <div className="text-xs text-faint">Keine Targets — Erlös bleibt Cash.</div>
          )}
          {reinvestTargets.map((t, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <select
                className="flex-1 border border-hairline bg-void px-2 py-1.5 font-mono text-xs text-ink focus:border-bernstein focus:outline-none"
                value={t.is_cash ? 'cash' : t.isin ? `new:${t.isin}` : String(t.position_id ?? '')}
                onChange={(e) => {
                  const v = e.target.value
                  if (v === 'cash') {
                    updateTarget(idx, { position_id: null, isin: null, is_cash: true })
                  } else if (v.startsWith('new:')) {
                    updateTarget(idx, { position_id: null, isin: v.slice(4), is_cash: false })
                  } else {
                    updateTarget(idx, { position_id: Number(v), isin: null, is_cash: false })
                  }
                }}
              >
                <option value="cash">Cash</option>
                {positions
                  .filter((p) => p.id !== selectedPositionId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.ticker ?? p.name}
                    </option>
                  ))}
                {newAssetOptions.map((o) => (
                  <option
                    key={o.isin}
                    value={`new:${o.isin}`}
                    aria-label={`${o.label} (Neues Asset)`}
                  >
                    {o.label}
                  </option>
                ))}
              </select>
              {t.isin && (
                <span className="tracked-caps border border-bernstein px-1.5 py-0.5 text-[9px] text-bernstein">
                  Neu
                </span>
              )}
              <TargetPctInput
                pct={t.pct}
                onPctChange={(next) => updateTarget(idx, { pct: next })}
              />
              <span className="text-xs text-muted">%</span>
              <button
                type="button"
                onClick={() => removeTarget(idx)}
                className="px-1 text-xs text-loss transition-colors hover:text-bernstein"
                aria-label="Target entfernen"
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" onClick={addTarget} className={SECONDARY_BTN}>
            + Target
          </button>
        </div>
      </div>
    </div>
  )
}

interface ReinvestTotalProps {
  targets: ReinvestTargetInput[]
}

function ReinvestTotal({ targets }: ReinvestTotalProps) {
  if (targets.length === 0) return null
  const total = targets.reduce((sum, t) => sum + Number(t.pct), 0)
  const pct = Math.round(total * 1000) / 10
  const isOver = total > 1.0001
  const isUnder = total < 0.9999
  const label = isOver ? 'über 100% — Backend lehnt ab' : isUnder ? 'Rest wird Cash' : 'voll'
  const cls = isOver ? 'text-loss' : isUnder ? 'text-muted' : 'text-gain'
  return (
    <div className={`mt-1 font-mono text-[11px] ${cls}`}>
      Σ {pct.toFixed(1)}% <span className="text-faint">— {label}</span>
    </div>
  )
}

interface SellPctInputProps {
  sellPct: string
  onSellPctChange: (pct: string) => void
}

function SellPctInput({ sellPct, onSellPctChange }: SellPctInputProps) {
  const [display, setDisplay] = useState(() => shareToPctDisplay(sellPct))

  // Pull external changes (URL deep-link, reset) into local state, but only when
  // the parent share genuinely differs from what we're showing — never on the
  // float-drift round-trip from our own onChange.
  useEffect(() => {
    const expectedShare = pctDisplayToShare(display)
    if (Math.abs(Number(expectedShare) - Number(sellPct)) > 0.0001) {
      setDisplay(shareToPctDisplay(sellPct))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellPct])

  return (
    <div>
      <label className="tracked-caps text-[10px] text-muted">Verkaufs-Menge (% des Bestands)</label>
      <input
        type="number"
        min="0"
        max="100"
        step="0.1"
        value={display}
        onChange={(e) => {
          setDisplay(e.target.value)
          onSellPctChange(pctDisplayToShare(e.target.value))
        }}
        placeholder="z.B. 30"
        className={`mt-2 ${INPUT_CLS}`}
      />
    </div>
  )
}

interface TargetPctInputProps {
  pct: string
  onPctChange: (pct: string) => void
}

function TargetPctInput({ pct, onPctChange }: TargetPctInputProps) {
  const [display, setDisplay] = useState(() => shareToPctDisplay(pct))

  useEffect(() => {
    const expectedShare = pctDisplayToShare(display)
    if (Math.abs(Number(expectedShare) - Number(pct)) > 0.0001) {
      setDisplay(shareToPctDisplay(pct))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pct])

  return (
    <input
      type="number"
      min="0"
      max="100"
      step="0.1"
      value={display}
      onChange={(e) => {
        setDisplay(e.target.value)
        onPctChange(pctDisplayToShare(e.target.value))
      }}
      placeholder="0"
      className="w-20 border border-hairline bg-void px-2 py-1.5 font-mono text-xs text-ink placeholder:text-faint focus:border-bernstein focus:outline-none"
    />
  )
}
